/**
 * Cierre de sesión: reflexión breve y correcciones útiles.
 *
 * NO es un diario. Son 30–90 segundos: unos pocos deslizadores de las áreas
 * que ESTA sesión entrena, la confianza aparte, qué costó más, y las tres
 * correcciones de ChatGPT si la sesión las tiene.
 *
 * Nada es obligatorio. Obligar a rellenar campos después de entrenar es la
 * forma más rápida de que alguien deje de cerrar sus sesiones.
 */

import { AREAS, NOMBRE_AREA, type Area } from '../../domain/modelo/areas';
import type { CorreccionesTop3, CorreccionTop } from '../../domain/modelo/sesion';
import estilos from './CierreSesion.module.css';

interface Props {
  areasAEvaluar: readonly Area[];
  autoevaluaciones: Record<string, number>;
  onCambiarAutoevaluacion: (area: Area, valor: number) => void;
  confianza: number | null;
  onCambiarConfianza: (valor: number) => void;
  masDificilArea: Area | null;
  onCambiarMasDificilArea: (area: Area | null) => void;
  masDificilNota: string;
  onCambiarMasDificilNota: (texto: string) => void;
  /** Solo si la sesión declara `capturaCorrecciones`. */
  correcciones: CorreccionesTop3 | null;
  onCambiarCorreccion: (
    cual: keyof CorreccionesTop3,
    cambios: Partial<CorreccionTop>,
  ) => void;
  feedbackCrudo: string;
  onCambiarFeedbackCrudo: (texto: string) => void;
  /** Solo en baseline y checkpoints: el resumen longitudinal. */
  pideResumen: boolean;
  resumenChatGPT: string;
  onCambiarResumenChatGPT: (texto: string) => void;
  /** Solo en el Día 6. */
  pidePersonaReal: boolean;
  conPersonaReal: boolean | null;
  onCambiarConPersonaReal: (valor: boolean) => void;
  usoRespaldoIA: boolean;
  onCambiarUsoRespaldoIA: (valor: boolean) => void;
}

function Escala({
  valor,
  onCambiar,
  nombre,
  etiqueta,
}: {
  valor: number | null;
  onCambiar: (v: number) => void;
  nombre: string;
  etiqueta: string;
}) {
  return (
    <div className={estilos.valoracion}>
      <span className={estilos.nombreArea}>{etiqueta}</span>
      <div className={estilos.escala} role="group" aria-label={etiqueta}>
        {[1, 2, 3, 4, 5].map((n) => (
          <label
            key={n}
            className={valor === n ? `${estilos.punto} ${estilos.puntoActivo}` : estilos.punto}
          >
            <input
              type="radio"
              name={nombre}
              value={n}
              checked={valor === n}
              onChange={() => onCambiar(n)}
              className="sr-only"
            />
            {n}
          </label>
        ))}
      </div>
    </div>
  );
}

const CORRECCIONES: {
  clave: keyof CorreccionesTop3;
  titulo: string;
  area: Area;
  ayuda: string;
}[] = [
  {
    clave: 'pronunciacion',
    titulo: 'Pronunciación y prosodia',
    area: 'pronunciacion_prosodia',
    ayuda: 'Un sonido, el ritmo, el acento de una palabra.',
  },
  {
    clave: 'naturalidad',
    titulo: 'Naturalidad y word choice',
    area: 'naturalidad',
    ayuda: 'Algo que se entiende pero no suena como lo diría un hablante.',
  },
  {
    clave: 'gramatica',
    titulo: 'Gramática y fluidez',
    area: 'gramatica_fluidez',
    ayuda: 'Una estructura que te frenó o salió mal.',
  },
];

export function CierreSesion(props: Props) {
  const {
    areasAEvaluar,
    autoevaluaciones,
    onCambiarAutoevaluacion,
    confianza,
    onCambiarConfianza,
    masDificilArea,
    onCambiarMasDificilArea,
    masDificilNota,
    onCambiarMasDificilNota,
    correcciones,
    onCambiarCorreccion,
    feedbackCrudo,
    onCambiarFeedbackCrudo,
    pideResumen,
    resumenChatGPT,
    onCambiarResumenChatGPT,
    pidePersonaReal,
    conPersonaReal,
    onCambiarConPersonaReal,
    usoRespaldoIA,
    onCambiarUsoRespaldoIA,
  } = props;

  return (
    <div className={estilos.cierre}>
      {pidePersonaReal ? (
        <section className={estilos.seccion}>
          <h2 className={estilos.titulo}>¿Cómo fue hoy?</h2>
          <div className={estilos.opcionesBinarias}>
            <button
              type="button"
              className={
                conPersonaReal === true
                  ? `${estilos.binario} ${estilos.binarioActivo}`
                  : estilos.binario
              }
              aria-pressed={conPersonaReal === true}
              onClick={() => onCambiarConPersonaReal(true)}
            >
              Hablé con una persona real
            </button>
            <button
              type="button"
              className={
                conPersonaReal === false
                  ? `${estilos.binario} ${estilos.binarioActivo}`
                  : estilos.binario
              }
              aria-pressed={conPersonaReal === false}
              onClick={() => onCambiarConPersonaReal(false)}
            >
              No conseguí a nadie
            </button>
          </div>

          {conPersonaReal === false ? (
            <label className={estilos.casilla}>
              <input
                type="checkbox"
                checked={usoRespaldoIA}
                onChange={(e) => onCambiarUsoRespaldoIA(e.target.checked)}
              />
              <span>Usé ChatGPT como respaldo</span>
            </label>
          ) : null}

          {conPersonaReal === false && usoRespaldoIA ? (
            <p className={estilos.aviso}>
              Queda registrado como respaldo, no como conversación humana. No son lo
              mismo, y el progreso tiene que reflejar eso.
            </p>
          ) : null}
        </section>
      ) : null}

      {areasAEvaluar.length > 0 ? (
        <section className={estilos.seccion}>
          <h2 className={estilos.titulo}>¿Cómo lo sentiste?</h2>
          <p className={estilos.explicacion}>
            <strong>1</strong> = me costó mucho · <strong>5</strong> = me salió con
            facilidad. Es tu percepción, no una nota.
          </p>

          {areasAEvaluar.map((area) => (
            <Escala
              key={area}
              nombre={`eval-${area}`}
              etiqueta={NOMBRE_AREA[area]}
              valor={autoevaluaciones[area] ?? null}
              onCambiar={(v) => onCambiarAutoevaluacion(area, v)}
            />
          ))}

          {/* Confianza va APARTE: es un estado, no una habilidad, y no es
              una octava área. Nunca entra en la puntuación del Día 5. */}
          <div className={estilos.separador} />
          <p className={estilos.explicacion}>
            ¿Qué tan cómodo te sentiste hoy hablando e interactuando?
          </p>
          <Escala
            nombre="eval-confianza"
            etiqueta="Confianza"
            valor={confianza}
            onCambiar={onCambiarConfianza}
          />
        </section>
      ) : null}

      <section className={estilos.seccion}>
        <h2 className={estilos.titulo}>¿Qué fue lo más difícil de hoy?</h2>
        <p className={estilos.explicacion}>
          Opcional, pero es una de las señales que usa el Día 5 para recomendarte un
          foco.
        </p>

        <div className={estilos.chips} role="group" aria-label="Lo más difícil de hoy">
          {AREAS.map((area) => (
            <button
              key={area}
              type="button"
              className={
                masDificilArea === area ? `${estilos.chip} ${estilos.chipActivo}` : estilos.chip
              }
              aria-pressed={masDificilArea === area}
              onClick={() => onCambiarMasDificilArea(masDificilArea === area ? null : area)}
            >
              {NOMBRE_AREA[area]}
            </button>
          ))}
        </div>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Una frase, si querés</span>
          <input
            className={estilos.input}
            value={masDificilNota}
            onChange={(e) => onCambiarMasDificilNota(e.target.value)}
            placeholder="qué pasó exactamente"
          />
        </label>
      </section>

      {pideResumen ? (
        <section className={estilos.seccion}>
          <h2 className={estilos.titulo}>Resumen de ChatGPT</h2>
          <p className={estilos.explicacion}>
            Si le pediste un resumen al final, pegalo acá. Se guarda tal cual, junto a
            esta medición, para poder releerlo al lado del de W4, W8 y W12. La
            aplicación no lo interpreta ni saca métricas de él.
          </p>
          <textarea
            className={estilos.texto}
            rows={5}
            value={resumenChatGPT}
            onChange={(e) => onCambiarResumenChatGPT(e.target.value)}
            placeholder="pegá acá el resumen, si lo tenés"
            aria-label="Resumen de ChatGPT"
          />
        </section>
      ) : null}

      {correcciones ? (
        <section className={estilos.seccion}>
          <h2 className={estilos.titulo}>Las tres correcciones de ChatGPT</h2>
          <p className={estilos.explicacion}>
            Solo las que te dio de verdad. Si no hubo ninguna en alguna categoría,
            dejala vacía: no hace falta inventar una. Lo que escribas acá es la
            respuesta que el Día 4 te va a esconder hasta que lo intentes.
          </p>

          {CORRECCIONES.map(({ clave, titulo, area, ayuda }) => {
            const valor = correcciones[clave];
            const bloqueada = clave === 'pronunciacion' && valor.noConfiable;
            return (
              <div key={clave} className={estilos.correccion}>
                <span className={estilos.nombreArea}>{titulo}</span>
                <span className={estilos.ayuda}>{ayuda}</span>

                <textarea
                  className={estilos.texto}
                  rows={2}
                  value={valor.texto}
                  disabled={bloqueada}
                  onChange={(e) => onCambiarCorreccion(clave, { texto: e.target.value })}
                  placeholder="qué te corrigió, tal como te lo dijo"
                  aria-label={titulo}
                />

                {/* El contexto es lo que convierte esto en retrieval: sin él
                    hay respuesta, pero nada desde donde recuperarla. Opcional
                    a propósito — obligarlo haría que se rellene por rellenar. */}
                <input
                  className={estilos.input}
                  value={valor.contexto ?? ''}
                  disabled={bloqueada || !valor.texto.trim()}
                  onChange={(e) => onCambiarCorreccion(clave, { contexto: e.target.value })}
                  placeholder="qué estabas intentando decir (opcional)"
                  aria-label={`Contexto · ${titulo}`}
                />

                {clave === 'pronunciacion' ? (
                  <label className={estilos.casilla}>
                    <input
                      type="checkbox"
                      checked={Boolean(valor.noConfiable)}
                      onChange={(e) =>
                        onCambiarCorreccion(clave, {
                          noConfiable: e.target.checked,
                          ...(e.target.checked ? { alBanco: false } : {}),
                        })
                      }
                    />
                    <span>
                      No es confiable — ChatGPT no puede juzgar bien mi pronunciación
                    </span>
                  </label>
                ) : null}

                <label className={estilos.casilla}>
                  <input
                    type="checkbox"
                    checked={valor.alBanco}
                    disabled={bloqueada || !valor.texto.trim()}
                    onChange={(e) => onCambiarCorreccion(clave, { alBanco: e.target.checked })}
                  />
                  <span>
                    Guardar en el Learning Bank como {NOMBRE_AREA[area].toLowerCase()}
                  </span>
                </label>
              </div>
            );
          })}

          <details className={estilos.crudo}>
            <summary>Pegar el feedback completo de ChatGPT</summary>
            <p className={estilos.explicacion}>
              Se guarda tal cual, como referencia. La aplicación no lo interpreta ni
              saca categorías de él: eso lo confirmás vos arriba.
            </p>
            <textarea
              className={estilos.texto}
              rows={4}
              value={feedbackCrudo}
              onChange={(e) => onCambiarFeedbackCrudo(e.target.value)}
              aria-label="Feedback completo de ChatGPT"
            />
          </details>
        </section>
      ) : null}
    </div>
  );
}

export { CORRECCIONES as CATEGORIAS_CORRECCION };
